import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  ButtonGroup,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  TextField,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  ShoppingCart as ShoppingCartIcon,
  ErrorOutline as ErrorOutlineIcon,
  WarningAmber as WarningAmberIcon,
  PhoneCallback as PhoneCallbackIcon,
} from "@mui/icons-material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { ThemeContext } from "./context/ThemeContext";
import api from "../api/api";
import DashboardOverview from "./DashboardOverview";
import { calendarDate } from '../../utils/calendarDate';

const Dashboard = () => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [period, setPeriod] = useState("month"); // 'day', 'month', 'year', 'all', 'custom'
  // Виджет закупок
  const [procurementStats, setProcurementStats] = useState({
    need_procurement: 0,
    total_assets: 0,
  });
  // Виджет просроченных/истекающих сделок
  const [expiringDeals, setExpiringDeals] = useState({
    overdue_count: 0,
    today_count: 0,
    total_attention: 0,
    preview: [],
  });
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [financialData, setFinancialData] = useState({
    summary: {
      total_income: 0,
      total_expense: 0,
      net_profit: 0,
      total_transactions: 0,
    },
    chartData: [],
    categoryData: [],
  });
  const [quickStats, setQuickStats] = useState({
    periodIncome: 0,
    periodExpense: 0,
    periodDeals: 0,
    periodClients: 0,
    totalAssets: 0,
  });
  const [chartMetrics, setChartMetrics] = useState({
    income: true,
    expense: true, // ✅ Включаем расходы по умолчанию
    balance: true, // Добавляем баланс как показатель графика
    clients: false,
    deals: false,
  });

  // Загрузка данных при изменении периода или дат
  useEffect(() => {
    fetchAnalyticsData();
  }, [period, customDateFrom, customDateTo]);

  // Загрузка статистики закупок при монтировании
  useEffect(() => {
    api
      .get("/procurement/stats")
      .then((res) => setProcurementStats(res.data))
      .catch(() => {});
    // Загрузка просроченных/истекающих сделок
    api
      .get("/dashboard/expiring-deals")
      .then((res) => setExpiringDeals(res.data))
      .catch(() => {});
  }, []);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    setLoadError('');
    try {
      // Определяем даты для фильтрации
      let endDate = new Date();
      let startDate = new Date();

      if (period === "custom") {
        // Используем пользовательские даты
        if (customDateFrom && customDateTo) {
          startDate = new Date(`${customDateFrom}T00:00:00`);
          endDate = new Date(`${customDateTo}T00:00:00`);
          endDate.setHours(23, 59, 59, 999); // Конец дня
        } else {
          // Если даты не выбраны, используем текущий месяц
          startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
        }
      } else if (period === "day") {
        // Текущий день - с начала дня до конца дня
        startDate = new Date();
        startDate.setHours(0, 0, 0, 0); // Начало дня
        endDate = new Date();
        endDate.setHours(23, 59, 59, 999); // Конец дня
      } else if (period === "month") {
        // 30 дней назад от сегодня (сегодня — последний день на графике)
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 29);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
      } else if (period === "year") {
        // 12 месяцев назад от текущего (текущий месяц — последний на графике)
        startDate = new Date();
        startDate.setDate(1);
        startDate.setMonth(startDate.getMonth() - 11);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
      } else {
        startDate = null; // Все время
        endDate = null;
      }

      // 🚀 НОВЫЙ ОПТИМИЗИРОВАННЫЙ ПОДХОД: Один запрос вместо 11
      const params = new URLSearchParams();
      if (startDate) {
        params.append("date_from", startDate.toISOString());
      }
      if (endDate) {
        params.append("date_to", endDate.toISOString());
      }

      // Один запрос для всех данных Dashboard
      const response = await api.get(`/dashboard/stats?${params}`);
      const { quick_stats, chart_data, summary } = response.data;

      // Обрабатываем данные из chart_data для графиков
      const transactions = chart_data.transactions || [];
      const deals = chart_data.deals || [];
      const clients = chart_data.clients || [];
      const expenses = chart_data.expenses || [];

      // Обрабатываем данные для графика - бэкенд уже агрегировал по дням
      const chartData = processOptimizedChartData(
        transactions,
        deals,
        clients,
        expenses,
      );
      const categoryData = []; // Убираем fake категории расходов

      setFinancialData({
        summary,
        chartData,
        categoryData,
      });

      // Используем quick_stats из оптимизированного эндпоинта
      setQuickStats(quick_stats);
    } catch (error) {
      setLoadError('Не удалось загрузить аналитику. Попробуйте ещё раз.');
      console.error("❌ DASHBOARD - Ошибка загрузки аналитики:", error);
      console.error("❌ DASHBOARD - Детали ошибки:", {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      });
    } finally {
      setLoading(false);
    }
  };

  // Расчёты старого формата вынесены в dashboardLegacyCalculations.js.
  // 🚀 НОВАЯ функция для обработки оптимизированных данных из бэкенда
  const processOptimizedChartData = (
    transactions,
    deals,
    clients,
    expenses,
  ) => {
    const dataByDate = {};
    const now = new Date();

    // Функция для нормализации даты к нужному ключу в зависимости от периода
    const getDateKey = (dateStr) => {
      const date = new Date(dateStr);
      if (period === "day") {
        // Для дня: ключ = дата + час
        const hour = date.getHours().toString().padStart(2, "0");
        return `${calendarDate(date)}T${hour}:00:00`;
      } else if (period === "month" || period === "custom") {
        // Для месяца / custom: ключ = только дата (YYYY-MM-DD)
        const y = date.getFullYear();
        const m = (date.getMonth() + 1).toString().padStart(2, "0");
        const d = date.getDate().toString().padStart(2, "0");
        return `${y}-${m}-${d}`;
      } else if (period === "year" || period === "all") {
        // Для года/все время: ключ = первое число месяца (YYYY-MM-01)
        return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-01`;
      }
      return dateStr;
    };

    // 1. Сначала заполняем все периоды нулями в зависимости от выбранного периода
    if (period === "day") {
      // Все часы текущего дня (0-23)
      for (let h = 0; h < 24; h++) {
        const dateKey = `${calendarDate(now)}T${h.toString().padStart(2, "0")}:00:00`;
        dataByDate[dateKey] = {
          date: dateKey,
          income: 0,
          expenses: 0,
          deals: 0,
          clients: 0,
          expensesAmount: 0,
        };
      }
    } else if (period === "month") {
      // 30 дней: от (сегодня - 29 дней) до сегодня
      for (let i = 29; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const dateKey = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
        dataByDate[dateKey] = {
          date: dateKey,
          income: 0,
          expenses: 0,
          deals: 0,
          clients: 0,
          expensesAmount: 0,
        };
      }
    } else if (period === "year") {
      // 12 месяцев: от (текущий - 11 мес) до текущего
      for (let i = 11; i >= 0; i--) {
        const d = new Date();
        d.setDate(1);
        d.setMonth(d.getMonth() - i);
        const dateKey = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-01`;
        dataByDate[dateKey] = {
          date: dateKey,
          income: 0,
          expenses: 0,
          deals: 0,
          clients: 0,
          expensesAmount: 0,
        };
      }
    } else if (period === "custom") {
      // Свой период: генерируем дни от customDateFrom до customDateTo
      if (customDateFrom && customDateTo) {
        const start = new Date(`${customDateFrom}T00:00:00`);
        const end = new Date(`${customDateTo}T00:00:00`);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const dateKey = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
          dataByDate[dateKey] = {
            date: dateKey,
            income: 0,
            expenses: 0,
            deals: 0,
            clients: 0,
            expensesAmount: 0,
          };
        }
      }
    } else if (period === "all") {
      // Для "Все время" заполняем все месяцы из диапазона данных
      const allDates = [...transactions, ...deals, ...clients, ...expenses].map(
        (item) => new Date(item.date),
      );
      if (allDates.length > 0) {
        const minDate = new Date(Math.min(...allDates));
        const maxDate = new Date(Math.max(...allDates));

        // Генерируем все месяцы между min и max
        let currentDate = new Date(
          minDate.getFullYear(),
          minDate.getMonth(),
          1,
        );
        const endDate = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

        while (currentDate <= endDate) {
          const dateKey = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, "0")}-01`;
          dataByDate[dateKey] = {
            date: dateKey,
            income: 0,
            expenses: 0,
            deals: 0,
            clients: 0,
            expensesAmount: 0,
          };
          currentDate.setMonth(currentDate.getMonth() + 1);
        }
      }
    }

    // 2. Теперь заполняем реальные данные поверх нулей (с нормализацией ключа)
    // ВАЖНО: Игнорируем данные вне периода (не создаем новые ключи)

    // Обрабатываем транзакции (только доходы - income)
    transactions.forEach((t) => {
      const dateKey = getDateKey(t.date);
      if (dataByDate[dateKey]) {
        // Только если ключ уже существует
        if (t.type === "income") {
          dataByDate[dateKey].income += t.amount;
        }
      }
    });

    // Обрабатываем сделки
    deals.forEach((d) => {
      const dateKey = getDateKey(d.date);
      if (dataByDate[dateKey]) {
        // Только если ключ уже существует
        dataByDate[dateKey].deals += d.count;
      }
    });

    // Обрабатываем клиентов
    clients.forEach((c) => {
      const dateKey = getDateKey(c.date);
      if (dataByDate[dateKey]) {
        // Только если ключ уже существует
        dataByDate[dateKey].clients += c.count;
      }
    });

    // Обрабатываем РЕАЛЬНЫЕ расходы из таблицы Expense
    expenses.forEach((e) => {
      const dateKey = getDateKey(e.date);
      if (dataByDate[dateKey]) {
        // Только если ключ уже существует
        dataByDate[dateKey].expensesAmount += e.amount;
        dataByDate[dateKey].expenses += e.amount;
      }
    });

    // 3. Конвертируем в массив, добавляем баланс и сортируем по дате
    return Object.values(dataByDate)
      .map((item) => ({
        ...item,
        balance: item.income - item.expenses,
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // Старая функция (оставляем для совместимости, но уже не используется)
  const toggleChartMetric = (metric) => {
    setChartMetrics((prev) => ({
      ...prev,
      [metric]: !prev[metric],
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: "RUB",
    }).format(value);
  };

  // Форматирование даты для оси X графика
  const formatXAxisDate = (dateStr) => {
    const date = new Date(dateStr);
    const months = [
      "Янв",
      "Фев",
      "Мар",
      "Апр",
      "Май",
      "Июн",
      "Июл",
      "Авг",
      "Сен",
      "Окт",
      "Ноя",
      "Дек",
    ];

    if (period === "day") {
      // Для дня: показываем часы (00:00, 06:00, 12:00, 18:00...)
      return date.toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (period === "month") {
      // Для месяца: показываем дни (01, 05, 10, 15, 20, 25, 30)
      return date.getDate().toString();
    } else if (period === "year") {
      // Для года: показываем месяцы (Янв, Фев, Мар...)
      return months[date.getMonth()];
    } else if (period === "all") {
      // Для "Все время": месяц + год (Янв 2024, Фев 2024...)
      return `${months[date.getMonth()]} ${date.getFullYear()}`;
    }

    // Для custom периода - короткий формат даты
    return date.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#8884D8",
    "#82CA9D",
  ];

  return (
    <Box className="erp-dashboard" sx={{ padding: 3 }}>
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {/* Заголовок и период */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Дашборд
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <ButtonGroup variant="outlined" size="small">
            <Button
              variant={period === "day" ? "contained" : "outlined"}
              onClick={() => setPeriod("day")}
            >
              День
            </Button>
            <Button
              variant={period === "month" ? "contained" : "outlined"}
              onClick={() => setPeriod("month")}
            >
              Месяц
            </Button>
            <Button
              variant={period === "year" ? "contained" : "outlined"}
              onClick={() => setPeriod("year")}
            >
              Год
            </Button>
            <Button
              variant={period === "all" ? "contained" : "outlined"}
              onClick={() => setPeriod("all")}
            >
              Все время
            </Button>
            <Button
              variant={period === "custom" ? "contained" : "outlined"}
              onClick={() => setPeriod("custom")}
            >
              Свой период
            </Button>
          </ButtonGroup>

          {/* Поля выбора дат для пользовательского периода */}
          {period === "custom" && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                label="От"
                type="date"
                size="small"
                value={customDateFrom}
                onChange={(e) => setCustomDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 140 }}
              />
              <TextField
                label="До"
                type="date"
                size="small"
                value={customDateTo}
                onChange={(e) => setCustomDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 140 }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Быстрые кнопки - статистика */}
          <Grid className="erp-dashboard-kpis" container spacing={3} sx={{ mb: 4 }}>
            {/* Доходы */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #0878ff 0%, #244a77 100%)",
                  color: "white",
                  borderRadius: 4,
                  boxShadow: "0 8px 32px rgba(102,126,234,0.35)",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(quickStats.periodIncome)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Доход{" "}
                        {period === "day"
                          ? "за день"
                          : period === "month"
                            ? "за месяц"
                            : period === "year"
                              ? "за год"
                              : period === "custom"
                                ? "за период"
                                : "за все время"}
                      </Typography>
                    </Box>
                    <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Расходы */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)",
                  color: "white",
                  borderRadius: 4,
                  boxShadow: "0 8px 32px rgba(255,107,107,0.35)",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(quickStats.periodExpense)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Расходы{" "}
                        {period === "day"
                          ? "за день"
                          : period === "month"
                            ? "за месяц"
                            : period === "year"
                              ? "за год"
                              : period === "custom"
                                ? "за период"
                                : "за все время"}
                      </Typography>
                    </Box>
                    <TrendingDownIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Новые клиенты */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                  color: "white",
                  borderRadius: 4,
                  boxShadow: "0 8px 32px rgba(79,172,254,0.35)",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {quickStats.periodClients}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Новых клиентов{" "}
                        {period === "day"
                          ? "за день"
                          : period === "month"
                            ? "за месяц"
                            : period === "year"
                              ? "за год"
                              : period === "custom"
                                ? "за период"
                                : "за все время"}
                      </Typography>
                    </Box>
                    <AssessmentIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Аренды */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                  color: "white",
                  borderRadius: 4,
                  boxShadow: "0 8px 32px rgba(250,112,154,0.35)",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {quickStats.periodDeals}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Аренд{" "}
                        {period === "day"
                          ? "за день"
                          : period === "month"
                            ? "за месяц"
                            : period === "year"
                              ? "за год"
                              : period === "custom"
                                ? "за период"
                                : "за все время"}
                      </Typography>
                    </Box>
                    <AccountBalanceIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Виджет закупок */}
          {procurementStats.need_procurement > 0 && (
            <Card
              sx={{
                mb: 3,
                background: "linear-gradient(135deg, #ff8f00 0%, #f57c00 100%)",
                color: "white",
                cursor: "pointer",
                borderRadius: 4,
                boxShadow: "0 8px 32px rgba(255,143,0,0.30)",
              }}
              onClick={() => navigate("/procurement")}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <ShoppingCartIcon sx={{ fontSize: 36, opacity: 0.9 }} />
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        🛒 Нужно закупить: {procurementStats.need_procurement}{" "}
                        позиц.
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        Некоторые активы заканчиваются или заняты — нажмите
                        чтобы посмотреть рекомендации
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.25)",
                      color: "white",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.4)" },
                      whiteSpace: "nowrap",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/procurement");
                    }}
                  >
                    Перейти →
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Виджет просроченных/истекающих сделок */}
          {expiringDeals.total_attention > 0 && (
            <Card
              sx={{
                mb: 3,
                background:
                  expiringDeals.overdue_count > 0
                    ? "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)"
                    : "linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)",
                color: "white",
                borderRadius: 4,
                boxShadow:
                  expiringDeals.overdue_count > 0
                    ? "0 8px 32px rgba(211,47,47,0.30)"
                    : "0 8px 32px rgba(245,124,0,0.30)",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: expiringDeals.preview.length > 0 ? 2 : 0,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <PhoneCallbackIcon sx={{ fontSize: 36, opacity: 0.9 }} />
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        📞 Нужно позвонить: {expiringDeals.total_attention}{" "}
                        клиент
                        {expiringDeals.total_attention === 1
                          ? ""
                          : expiringDeals.total_attention < 5
                            ? "а"
                            : "ов"}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {expiringDeals.overdue_count > 0 &&
                          `Просрочено: ${expiringDeals.overdue_count}`}
                        {expiringDeals.overdue_count > 0 &&
                          expiringDeals.today_count > 0 &&
                          " · "}
                        {expiringDeals.today_count > 0 &&
                          `Заканчивается сегодня: ${expiringDeals.today_count}`}
                        {" — "}нажмите чтобы увидеть все в списке сделок
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.25)",
                      color: "white",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.4)" },
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => navigate("/rental-deals?filter=expiring")}
                  >
                    Все сделки →
                  </Button>
                </Box>
                {/* Превью: список сделок */}
                {expiringDeals.preview.length > 0 && (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {expiringDeals.preview.map((deal) => (
                      <Box
                        key={deal.deal_id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          bgcolor: "rgba(255,255,255,0.15)",
                          borderRadius: 1,
                          px: 2,
                          py: 0.8,
                          cursor: "pointer",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                        }}
                        onClick={() =>
                          navigate(`/rental-deals/edit/${deal.deal_id}`)
                        }
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            №{deal.deal_number}
                          </Typography>
                          <Typography variant="body2">
                            {deal.client_name}
                          </Typography>
                          {deal.client_phone && (
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                              📱 {deal.client_phone}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={deal.status}
                          size="small"
                          sx={{
                            bgcolor:
                              deal.status === "просрочена"
                                ? "rgba(255,0,0,0.3)"
                                : "rgba(255,165,0,0.3)",
                            color: "white",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          <DashboardOverview />
          {/* Графики */}
          <Grid container spacing={3}>
            {/* График доходов, расходов, клиентов и аренд */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 4,
                  boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    📈 Динамика показателей
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 2 }}>
                    Доход показан за вычетом завершённых возвратов. Возврат учитывается в день его выполнения.
                  </Typography>
                  {financialData.chartData.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={400}>
                        <AreaChart data={financialData.chartData}>
                          <defs>
                            <linearGradient
                              id="gradIncome"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#4caf50"
                                stopOpacity={0.25}
                              />
                              <stop
                                offset="95%"
                                stopColor="#4caf50"
                                stopOpacity={0.02}
                              />
                            </linearGradient>
                            <linearGradient
                              id="gradExpense"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#f44336"
                                stopOpacity={0.25}
                              />
                              <stop
                                offset="95%"
                                stopColor="#f44336"
                                stopOpacity={0.02}
                              />
                            </linearGradient>
                            <linearGradient
                              id="gradBalance"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#9c27b0"
                                stopOpacity={0.25}
                              />
                              <stop
                                offset="95%"
                                stopColor="#9c27b0"
                                stopOpacity={0.02}
                              />
                            </linearGradient>
                            <linearGradient
                              id="gradClients"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#2196f3"
                                stopOpacity={0.25}
                              />
                              <stop
                                offset="95%"
                                stopColor="#2196f3"
                                stopOpacity={0.02}
                              />
                            </linearGradient>
                            <linearGradient
                              id="gradDeals"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#ff9800"
                                stopOpacity={0.25}
                              />
                              <stop
                                offset="95%"
                                stopColor="#ff9800"
                                stopOpacity={0.02}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="rgba(0,0,0,0.06)"
                          />
                          <XAxis
                            dataKey="date"
                            tickFormatter={formatXAxisDate}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis
                            tickFormatter={(value) =>
                              `${(value / 1000).toFixed(0)}к`
                            }
                          />
                          <Tooltip
                            contentStyle={{
                              borderRadius: 10,
                              border: "none",
                              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                            }}
                            formatter={(value, name) => {
                              if (
                                name === "Доходы" ||
                                name === "Расходы" ||
                                name === "Баланс"
                              ) {
                                return formatCurrency(value);
                              }
                              return value;
                            }}
                          />
                          <Legend />

                          {chartMetrics.income && (
                            <Area
                              type="monotone"
                              dataKey="income"
                              stroke="#4caf50"
                              strokeWidth={2.5}
                              fill="url(#gradIncome)"
                              name="Доходы"
                              dot={false}
                            />
                          )}

                          {chartMetrics.expense && (
                            <Area
                              type="monotone"
                              dataKey="expenses"
                              stroke="#f44336"
                              strokeWidth={2.5}
                              fill="url(#gradExpense)"
                              name="Расходы"
                              dot={false}
                            />
                          )}

                          {chartMetrics.balance && (
                            <Area
                              type="monotone"
                              dataKey="balance"
                              stroke="#9c27b0"
                              strokeWidth={2.5}
                              fill="url(#gradBalance)"
                              name="Баланс"
                              dot={false}
                            />
                          )}

                          {chartMetrics.clients && (
                            <Area
                              type="monotone"
                              dataKey="clients"
                              stroke="#2196f3"
                              strokeWidth={2.5}
                              fill="url(#gradClients)"
                              name="Клиенты"
                              dot={false}
                            />
                          )}

                          {chartMetrics.deals && (
                            <Area
                              type="monotone"
                              dataKey="deals"
                              stroke="#ff9800"
                              strokeWidth={2.5}
                              fill="url(#gradDeals)"
                              name="Аренды"
                              dot={false}
                            />
                          )}
                        </AreaChart>
                      </ResponsiveContainer>

                      {/* Кнопки выбора метрик */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          flexWrap: "wrap",
                          gap: 1,
                          mt: 2,
                        }}
                      >
                        <Chip
                          label="Доходы"
                          onClick={() => toggleChartMetric("income")}
                          color={chartMetrics.income ? "success" : "default"}
                          variant={chartMetrics.income ? "filled" : "outlined"}
                          sx={{ cursor: "pointer" }}
                        />
                        <Chip
                          label="Расходы"
                          onClick={() => toggleChartMetric("expense")}
                          color={chartMetrics.expense ? "error" : "default"}
                          variant={chartMetrics.expense ? "filled" : "outlined"}
                          sx={{ cursor: "pointer" }}
                        />
                        <Chip
                          label="Баланс"
                          onClick={() => toggleChartMetric("balance")}
                          color={chartMetrics.balance ? "secondary" : "default"}
                          variant={chartMetrics.balance ? "filled" : "outlined"}
                          sx={{ cursor: "pointer" }}
                        />
                        <Chip
                          label="Клиенты"
                          onClick={() => toggleChartMetric("clients")}
                          color={chartMetrics.clients ? "primary" : "default"}
                          variant={chartMetrics.clients ? "filled" : "outlined"}
                          sx={{ cursor: "pointer" }}
                        />
                        <Chip
                          label="Аренды"
                          onClick={() => toggleChartMetric("deals")}
                          color={chartMetrics.deals ? "warning" : "default"}
                          variant={chartMetrics.deals ? "filled" : "outlined"}
                          sx={{ cursor: "pointer" }}
                        />
                      </Box>
                    </>
                  ) : (
                    <Alert severity="info">
                      Данных для отображения графика пока нет
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default Dashboard;
